import { useMutation, useQuery } from "@tanstack/react-query";
import { apiGet, apiPost, ApiError } from "../../lib/api";
import type { ID } from "../id";

/**
 * Which of the three doors a club comes through. It decides the federation
 * the team is filed under and what the form asks for; nothing stores it.
 *
 * - `cmf_member` — a Czech club joining ČNSMS: the full data set.
 * - `cup_only` — a Czech club that only goes to pohárovky. Same data, same
 *   federation; whether it ever registers into a season is a later
 *   decision that nothing here records.
 * - `foreign` — a club from another state, filed under WMS: identity and
 *   contact only, no rodné číslo, billing optional.
 */
export type RegistrationFlow = "cmf_member" | "cup_only" | "foreign";

export interface RegisterTeamPayload {
  flow: RegistrationFlow;
  /**
   * Bot filter, not security (internal/handlers/api/registration.go): a
   * field no person sees, which must come back empty…
   */
  hp_check: string;
  /** …and how long the form was open before it was sent, in ms. */
  form_ms: number;
  team: {
    name: string;
    /** The state the club represents. The Czech flows send CZ. */
    country_code: string;
  };
  billing: {
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    subject_name: string;
    street: string;
    city: string;
    postal_code: string;
    country_code: string;
    ico: string;
    dic: string;
  };
  leader: {
    first_name: string;
    last_name: string;
    birth_date: string; // YYYY-MM-DD
    birth_number: string;
    country_code: string;
    street: string;
    city: string;
    postal_code: string;
    phone: string;
  };
  account: {
    // The only e-mail in the system: the login credential. The leader's
    // Person record deliberately carries none.
    email: string;
    password: string;
    display_name: string;
  };
}

/**
 * Where the active season's registration window stands, read by the public
 * chooser: a club that can still join ČNSMS for this season is told the date
 * it has to do it by, one that cannot is pointed at the next season.
 *
 * Its own endpoint, not `useSeason`: this page is filled in by somebody
 * with no account, so nothing session-scoped is available to it.
 */
export interface PublicSeason {
  code: string;
  phase: "pre_registration" | "registration_open" | "registration_closed";
  /** Date-only („2027-01-31"), deliberately not a timestamp. */
  registration_open_from?: string;
  registration_open_to?: string;
}

export function usePublicSeason() {
  return useQuery<PublicSeason>({
    queryKey: ["public", "season"],
    queryFn: () => apiGet<PublicSeason>("/registrace-tymu/sezona"),
    // A deployment with no active season answers 503; the page then says
    // nothing about dates rather than retrying at a club.
    retry: false,
  });
}

export interface RegisterTeamResponse {
  team_id: ID;
  person_id: ID;
  user_id: ID;
}

/** Public team-registration mutation; does not require an authenticated session. */
export function useRegisterTeam() {
  return useMutation<RegisterTeamResponse, ApiError, RegisterTeamPayload>({
    // The public page mounts no Toaster; TeamRegistrationPage binds every
    // error to the form itself (apiErrorToFormErrors).
    meta: { suppressErrorToast: true },
    mutationFn: (payload) => apiPost<RegisterTeamResponse>("/registrace-tymu", payload),
  });
}
